/* SB-009DM — Redesigned side cart A/B test controller.
 *
 * Public API (called by the A/B testing tool):
 *   window.ons009dm.init('v1')  -> reveal the V1 redesigned cart
 *   window.ons009dm.init('v2')  -> reveal the V2 redesigned cart
 *   window.ons009dm.refresh()   -> re-sync the V1 upsell carousel
 *
 * IMPORTANT: if init() is never called, nothing happens — the original cart
 * (.sc-control) renders untouched as the control experience. Both variant
 * bodies stay display:none (see ecomm-sc-v1.css / ecomm-sc-v2.css).
 *
 * A pre-load stub may queue a variant before this script loads:
 *   window.ons009dm = { _pending: 'v2' }
 */
(function () {
  'use strict';

  function root() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function cartStore() {
    return window.Alpine && typeof window.Alpine.store === 'function'
      ? window.Alpine.store('cart')
      : null;
  }

  /* ---- Money / savings helpers (used from Alpine bindings in the
   *      V1/V2 snippets, so they must live on window.ons009dm). ---- */

  function num(value) {
    var n = parseFloat(String(value == null ? '' : value).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  function fmt(n) {
    return (Math.round(n * 100) / 100).toFixed(2);
  }

  // Per-line savings in dollars: (_msrp - _price) × quantity.
  // NOTE: the _savings line-item property is a PERCENT in this theme
  // ("(X% Savings)" in the control cart), so it is never used as dollars.
  // The subscription checkout gift is excluded (its _msrp/_price are 0.00).
  function lineSavings(item) {
    if (!item || !item.properties) return 0;
    if (item.properties._subscription_checkout_gift) return 0;
    var m = num(item.properties._msrp);
    var p = num(item.properties._price);
    return m > p ? (m - p) * (item.quantity || 1) : 0;
  }

  function totalSavings(data) {
    if (!data || !data.items) return 0;
    return data.items.reduce(function (sum, item) {
      return sum + lineSavings(item);
    }, 0);
  }

  // Cart total in dollars (Shopify total_price is cents).
  function cartTotal(data) {
    return data ? data.total_price / 100 : 0;
  }

  // Discount % for the V2 off-badge: savings / (total + savings).
  function discountPct(data) {
    var save = totalSavings(data);
    var full = cartTotal(data) + save;
    if (save <= 0 || full <= 0) return 0;
    return Math.round((save / full) * 100);
  }

  // Family key: the "-add-on" products are separate Shopify products from
  // their main counterparts (skinny-gummies vs skinny-gummies-add-on), so
  // matching by product id alone misses them. Strip the suffix to compare.
  function familyKey(handle) {
    // Trim first: handles piped in from Liquid setting/product lists can carry a
    // stray leading/trailing space, which would otherwise break the family match
    // (e.g. " skinny-gummies" !== "skinny-gummies") and keep the block visible.
    return String(handle || '').trim().replace(/-add-on$/, '');
  }

  // True when the product — or its main/add-on counterpart — is already a
  // cart line item. Matches by product id (string-compared) or by handle
  // family. Used to hide upsell/cross-sell cards.
  function inCart(data, productId, handle) {
    if (!data || !data.items) return false;
    var targetId = String(productId);
    var targetFamily = familyKey(handle);
    return data.items.some(function (item) {
      if (String(item.product_id) === targetId) return true;
      return !!targetFamily && familyKey(item.handle) === targetFamily;
    });
  }

  // True when at least one of the upsell products is NOT in the cart —
  // drives visibility of the whole upsell/cross-sell block.
  // ids and handles are parallel arrays rendered by Liquid.
  function hasUpsellNotInCart(data, productIds, handles) {
    return (productIds || []).some(function (id, i) {
      return !inCart(data, id, handles && handles[i]);
    });
  }

  // Product id of the first upsell not yet in the cart, or null.
  // V2 shows a single cross-sell card: the first eligible product.
  function firstNotInCart(data, productIds, handles) {
    productIds = productIds || [];
    for (var i = 0; i < productIds.length; i++) {
      if (!inCart(data, productIds[i], handles && handles[i])) return productIds[i];
    }
    return null;
  }

  /* ---- Upsell add-to-cart ----
   * Deliberately NOT store.addItem(): theme.js addItem() toggles the drawer
   * open/closed after adding, which would close the cart the shopper is
   * looking at. Add via AJAX with the same line-item properties, then
   * refresh the Alpine store so every binding re-renders. */
  function addToCart(btn) {
    var d = btn.dataset;
    var variantId = parseInt(d.variantId, 10);
    if (!variantId || btn.disabled) return;

    var quantity = parseInt(d.quantity, 10) || 1;
    var sellingPlanId = d.sellingPlanId || '';

    btn.classList.add('is-loading');
    btn.disabled = true;

    var restore = function () {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    };

    var properties = {
      _image: d.image || '',
      _title: d.title || '',
      _msrp: d.msrp || '',
      _savings: d.savings || '',
      _price: d.price || ''
    };
    var item = { id: variantId, quantity: quantity, properties: properties };
    if (sellingPlanId) {
      item.selling_plan = parseInt(sellingPlanId, 10);
      properties._selling_plan_id = sellingPlanId;
    }

    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('add failed');
        var store = cartStore();
        if (store && typeof store.getCartData === 'function') {
          return store.getCartData();
        }
        window.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .then(restore, restore);
  }

  /* ---- V1 upsell carousel (dots + arrows) ---- */

  // Visible cards only — cards for products already in the cart are hidden
  // via Alpine x-show (inline display:none) and must not count for dots/arrows.
  function cards(el) {
    var track = el.querySelector('[data-sc-track]');
    if (!track) return [];
    return Array.prototype.slice.call(track.querySelectorAll('[data-sc-upsell]')).filter(function (card) {
      return card.style.display !== 'none';
    });
  }

  function activeIndex(el) {
    var track = el.querySelector('[data-sc-track]');
    if (!track) return 0;
    var list = cards(el);
    var best = 0;
    var min = Infinity;
    for (var i = 0; i < list.length; i++) {
      var delta = Math.abs(list[i].offsetLeft - track.scrollLeft);
      if (delta < min) {
        min = delta;
        best = i;
      }
    }
    return best;
  }

  function updateArrows(el) {
    var track = el.querySelector('[data-sc-track]');
    var prev = el.querySelector('[data-sc-prev]');
    var next = el.querySelector('[data-sc-next]');
    if (!track) return;
    var list = cards(el);
    // Track has no layout yet (parent still display:none when init() runs).
    // Fall back to card count so next isn't stuck disabled forever.
    if (!track.clientWidth) {
      if (prev) prev.disabled = true;
      if (next) next.disabled = list.length <= 1;
      return;
    }
    var maxScroll = track.scrollWidth - track.clientWidth - 1;
    if (prev) prev.disabled = track.scrollLeft <= 0;
    if (next) next.disabled = list.length <= 1 || track.scrollLeft >= maxScroll;
  }

  function updateDots(el) {
    var dotsEl = el.querySelector('[data-sc-dots]');
    if (!dotsEl) return;
    var dots = dotsEl.querySelectorAll('.sc-upsells-dot');
    var idx = activeIndex(el);
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('is-active', i === idx);
    }
  }

  function buildDots(el) {
    var dotsEl = el.querySelector('[data-sc-dots]');
    if (!dotsEl) return;
    var list = cards(el);
    dotsEl.innerHTML = '';
    if (list.length <= 1) {
      dotsEl.style.display = 'none';
      return;
    }
    dotsEl.style.display = '';
    list.forEach(function (card, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'sc-upsells-dot';
      dot.setAttribute('aria-label', 'Go to item ' + (i + 1));
      dot.addEventListener('click', function () {
        card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });
      dotsEl.appendChild(dot);
    });
    updateDots(el);
  }

  function scrollByCard(el, dir) {
    var track = el.querySelector('[data-sc-track]');
    if (!track) return;
    var first = cards(el)[0];
    var step = first ? first.getBoundingClientRect().width + 8 : track.clientWidth;
    track.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  function sync(el) {
    updateDots(el);
    updateArrows(el);
    syncEmptyState(el);
  }

  // Hide the whole upsell block (and its slot) when every card in the track
  // is display:none. Driven by DOM state rather than Alpine cart matching,
  // so a blank "Complete your routine" shell never stays visible.
  function syncEmptyState(el) {
    var empty = cards(el).length === 0;
    el.classList.toggle('is-empty', empty);
    var slot = el.closest ? el.closest('.sc-upsells-slot') : null;
    if (slot) slot.classList.toggle('is-empty', empty);
  }

  function mountCarousel(el) {
    if (el.dataset.scMounted === 'true') {
      buildDots(el);
      sync(el);
      return;
    }
    el.dataset.scMounted = 'true';

    var track = el.querySelector('[data-sc-track]');
    if (track) {
      var raf;
      track.addEventListener(
        'scroll',
        function () {
          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(function () { sync(el); });
        },
        { passive: true }
      );
    }

    var prev = el.querySelector('[data-sc-prev]');
    var next = el.querySelector('[data-sc-next]');
    if (prev) prev.addEventListener('click', function () { scrollByCard(el, -1); });
    if (next) next.addEventListener('click', function () { scrollByCard(el, 1); });

    window.addEventListener('resize', function () { sync(el); }, { passive: true });

    // Card visibility changes when products are added to / removed from the
    // cart (Alpine x-show toggles inline display) — rebuild dots + empty state.
    if (track && window.MutationObserver) {
      var observer = new MutationObserver(function () {
        buildDots(el);
        sync(el);
      });
      observer.observe(track, { attributes: true, attributeFilter: ['style'], subtree: true });
    }

    // Slot / block may start display:none (empty cart or pre-init). Re-sync
    // arrows once Alpine x-show reveals them and layout is measurable.
    if (window.MutationObserver) {
      var visibilityRoot = (el.closest && el.closest('.sc-upsells-slot')) || el;
      var visObserver = new MutationObserver(function () {
        buildDots(el);
        sync(el);
      });
      visObserver.observe(visibilityRoot, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    buildDots(el);
    sync(el);
  }

  function mountCarousels() {
    document.querySelectorAll('[data-sc-upsells]').forEach(mountCarousel);
  }

  // Double rAF: wait until after the browser has applied .ons009dm-v1 and laid
  // out the previously display:none cart so scrollWidth is real.
  function scheduleRefresh() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        api.refresh();
      });
    });
  }

  /* ---- Variant switching ---- */

  function applyVariant(variant) {
    document.querySelectorAll('#ecomm-side-cart').forEach(function (sec) {
      sec.classList.add('ons009dm-active');
      sec.classList.remove('ons009dm-v1', 'ons009dm-v2');
      sec.classList.add('ons009dm-' + variant);
    });
    if (variant === 'v1') {
      mountCarousels();
      scheduleRefresh();
    }
  }

  var api = {
    initialized: false,
    variant: null,
    fmt: fmt,
    lineSavings: lineSavings,
    totalSavings: totalSavings,
    cartTotal: cartTotal,
    discountPct: discountPct,
    inCart: inCart,
    hasUpsellNotInCart: hasUpsellNotInCart,
    firstNotInCart: firstNotInCart,
    // Called by the A/B tool: ons009dm.init('v1' | 'v2')
    init: function (variant) {
      this.variant = variant === 'v2' ? 'v2' : 'v1';
      this.initialized = true;
      applyVariant(this.variant);
      return this;
    },
    refresh: function () {
      document.querySelectorAll('[data-sc-upsells]').forEach(function (el) {
        buildDots(el);
        sync(el);
      });
      return this;
    }
  };

  // Preserve a variant queued by a pre-load stub.
  var pending = window.ons009dm && window.ons009dm._pending;
  window.ons009dm = api;

  function boot() {
    // Delegated add-to-cart for both variants. The control cart contains no
    // [data-sc-add] elements, so this listener never affects the control.
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-sc-add]') : null;
      if (btn) {
        e.preventDefault();
        addToCart(btn);
      }
    });

    // Keep the carousel in sync when the cart changes from anywhere else.
    window.addEventListener('cart:updated', function () {
      api.refresh();
      scheduleRefresh();
    });

    // Re-measure when the drawer slides open — init often ran while it was off-screen.
    var wrapper = document.getElementById('side-cart-wrapper');
    if (wrapper && window.MutationObserver) {
      new MutationObserver(function () {
        if (wrapper.classList.contains('open') && api.variant === 'v1') {
          scheduleRefresh();
        }
      }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
    }

    if (pending) api.init(pending);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
